import { useState, useEffect, useId, Children, isValidElement, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash, FloppyDisk, ShieldCheck } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const INDUSTRIES = ['Tollywood', 'Bollywood', 'Kollywood', 'Hollywood'];
const LANGUAGES = ['Telugu', 'Hindi', 'Tamil', 'Malayalam', 'Kannada', 'English', 'Bengali', 'Marathi', 'Punjabi'];
const EMPTY_PROJECT = { title: '', role: '', industry: '', year: '', status: 'past' };

const FraternityProfileEdit = ({ user }) => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [customRole, setCustomRole] = useState('');
  const [profile, setProfile] = useState({
    display_name: user?.name || '',
    headline: '',
    bio: '',
    primary_role: '',
    secondary_roles: [],
    industries: [],
    location: '',
    languages: [],
    years_experience: '',
    imdb_url: '',
    website: '',
    reel_url: '',
    photo_url: '',
    phone: '',
    email_public: '',
    open_for_work: true,
    imdb_credits_count: '',
    current_projects: [],
    upcoming_projects: [],
    past_projects: [],
  });
  const [verified, setVerified] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exists, setExists] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/fraternity/roles`).then((r) => setRoles(r.data.roles || []));
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${BACKEND_URL}/api/fraternity/profile/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => {
        if (r.data.exists) {
          setExists(true);
          setVerified(!!r.data.verified);
          setProfile({ ...profile, ...r.data.profile });
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleArrayItem = (key, item) => {
    const list = profile[key] || [];
    setProfile({ ...profile, [key]: list.includes(item) ? list.filter((x) => x !== item) : [...list, item] });
  };

  const addCustomRole = async () => {
    const r = customRole.trim();
    if (!r) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/fraternity/roles`, { role: r },
        { headers: { Authorization: `Bearer ${token}` } });
      if (!roles.includes(r)) setRoles([...roles, r]);
      setProfile({ ...profile, primary_role: r });
      setCustomRole('');
      toast.success(`Added "${r}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add role');
    }
  };

  const save = async () => {
    if (!profile.display_name?.trim()) { toast.error('Display name is required'); return; }
    if (!profile.primary_role) { toast.error('Pick a primary role'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body = { ...profile };
      // Coerce numeric strings
      if (body.years_experience === '') delete body.years_experience; else body.years_experience = Number(body.years_experience);
      if (body.imdb_credits_count === '') delete body.imdb_credits_count; else body.imdb_credits_count = Number(body.imdb_credits_count);
      // Drop empty project rows
      ['current_projects', 'upcoming_projects', 'past_projects'].forEach((k) => {
        body[k] = (body[k] || []).filter((p) => p.title?.trim() && p.role?.trim()).map((p) => ({
          ...p,
          year: p.year ? Number(p.year) : null,
        }));
      });
      const res = await axios.put(`${BACKEND_URL}/api/fraternity/profile/me`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.created ? 'Profile created' : 'Profile updated');
      setExists(true);
      navigate(`/fraternity/u/${user.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  const updateProject = (bucket, idx, field, value) => {
    const list = [...(profile[bucket] || [])];
    list[idx] = { ...list[idx], [field]: value };
    setProfile({ ...profile, [bucket]: list });
  };
  const addProject = (bucket, status) => {
    setProfile({ ...profile, [bucket]: [...(profile[bucket] || []), { ...EMPTY_PROJECT, status }] });
  };
  const removeProject = (bucket, idx) => {
    setProfile({ ...profile, [bucket]: (profile[bucket] || []).filter((_, i) => i !== idx) });
  };

  return (
    <div className="min-h-screen text-white" style={{ background: '#080414', fontFamily: "'Poppins', system-ui, sans-serif" }} data-testid="fraternity-edit-page">
      <header data-web-chrome="nav" className="border-b border-white/10 bg-black/40 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-sm text-white/70 hover:text-white flex items-center gap-1.5" data-testid="back-btn">
            <ArrowLeft size={14} /> Back
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-profile-btn"
            className="bg-white text-black font-bold text-sm px-5 py-2 rounded-full flex items-center gap-1.5 hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FloppyDisk size={14} weight="bold" /> {saving ? 'Saving…' : (exists ? 'Save changes' : 'Create profile')}
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ letterSpacing: '-0.03em' }}>
            {exists ? 'Edit your fraternity profile' : 'Create your fraternity profile'}
          </h1>
          {verified && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#22d3ee]/15 border border-[#22d3ee]/40 text-[11px] font-bold text-[#22d3ee]">
              <ShieldCheck size={12} weight="fill" /> Verified
            </span>
          )}
        </div>

        {/* Basics */}
        <Section title="Basics">
          <Row>
            <Field label="Display name *"><Input testid="input-display-name" value={profile.display_name || ''} onChange={(v) => setProfile({ ...profile, display_name: v })} /></Field>
            <Field label="Headline"><Input testid="input-headline" placeholder="Award-winning Director · 12+ years" value={profile.headline || ''} onChange={(v) => setProfile({ ...profile, headline: v })} /></Field>
          </Row>
          <Row>
            <Field label="Primary role *">
              <select data-testid="select-primary-role" aria-label="Primary role" value={profile.primary_role || ''} onChange={(e) => setProfile({ ...profile, primary_role: e.target.value })} className={inputCls}>
                <option value="">— pick —</option>
                {roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <div className="flex items-center gap-2 mt-2">
                <input data-testid="input-custom-role" aria-label="Add a new role" value={customRole} onChange={(e) => setCustomRole(e.target.value)} placeholder="Add a new role" className={`${inputCls} flex-1`} />
                <button data-testid="add-custom-role-btn" onClick={addCustomRole} className="bg-white/10 text-white text-xs px-3 py-2 rounded-lg border border-white/15 hover:bg-white/15">Add</button>
              </div>
            </Field>
            <Field label="Years of experience"><Input testid="input-years" type="number" min="0" value={profile.years_experience ?? ''} onChange={(v) => setProfile({ ...profile, years_experience: v })} /></Field>
          </Row>
          <Field label="Bio">
            <textarea data-testid="input-bio" value={profile.bio || ''} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} rows={4} className={`${inputCls} resize-none`} placeholder="Tell the industry who you are…" />
          </Field>
        </Section>

        {/* Where / What */}
        <Section title="Where & what you do">
          <Row>
            <Field label="Location"><Input testid="input-location" placeholder="Hyderabad, IN" value={profile.location || ''} onChange={(v) => setProfile({ ...profile, location: v })} /></Field>
            <Field label="Languages">
              <div className="flex flex-wrap gap-1.5" data-testid="languages-grid">
                {LANGUAGES.map((l) => (
                  <button key={l} type="button"
                    data-testid={`lang-${l}`}
                    onClick={() => toggleArrayItem('languages', l)}
                    className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border font-bold ${profile.languages?.includes(l) ? 'bg-[#c084fc]/25 border-[#c084fc] text-white' : 'bg-white/5 border-white/15 text-white/60 hover:border-white/30'}`}
                  >{l}</button>
                ))}
              </div>
            </Field>
          </Row>
          <Field label="Industries">
            <div className="flex flex-wrap gap-1.5" data-testid="industries-grid">
              {INDUSTRIES.map((i) => (
                <button key={i} type="button"
                  data-testid={`industry-${i}`}
                  onClick={() => toggleArrayItem('industries', i)}
                  className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border font-bold ${profile.industries?.includes(i) ? 'bg-[#facc15]/20 border-[#facc15] text-white' : 'bg-white/5 border-white/15 text-white/60 hover:border-white/30'}`}
                >{i}</button>
              ))}
            </div>
          </Field>
          <Field label="Other roles you take on">
            <div className="flex flex-wrap gap-1.5" data-testid="secondary-roles-grid">
              {roles.map((r) => (
                <button key={r} type="button"
                  data-testid={`secondary-${r.replace(/\s+/g,'-')}`}
                  onClick={() => toggleArrayItem('secondary_roles', r)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border ${profile.secondary_roles?.includes(r) ? 'bg-white/15 border-white/30 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'}`}
                >{r}</button>
              ))}
            </div>
          </Field>
        </Section>

        {/* Open for work + Links */}
        <Section title="Availability & links">
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-white/[0.04] border border-white/10 rounded-lg" data-testid="open-for-work-toggle-label">
            <input data-testid="open-for-work-toggle" type="checkbox" checked={!!profile.open_for_work} onChange={(e) => setProfile({ ...profile, open_for_work: e.target.checked })} className="w-4 h-4" />
            <div>
              <div className="text-sm font-bold">Open for new projects</div>
              <div className="text-[11px] text-white/70">Producers can filter the directory by "Open for work".</div>
            </div>
          </label>
          <Row>
            <Field label="Photo URL"><Input testid="input-photo" value={profile.photo_url || ''} onChange={(v) => setProfile({ ...profile, photo_url: v })} /></Field>
            <Field label="Website"><Input testid="input-website" value={profile.website || ''} onChange={(v) => setProfile({ ...profile, website: v })} /></Field>
          </Row>
          <Row>
            <Field label="IMDB URL"><Input testid="input-imdb" value={profile.imdb_url || ''} onChange={(v) => setProfile({ ...profile, imdb_url: v })} /></Field>
            <Field label="Showreel URL"><Input testid="input-reel" value={profile.reel_url || ''} onChange={(v) => setProfile({ ...profile, reel_url: v })} /></Field>
          </Row>
          <Row>
            <Field label="IMDB credits count"><Input testid="input-credits" type="number" min="0" value={profile.imdb_credits_count ?? ''} onChange={(v) => setProfile({ ...profile, imdb_credits_count: v })} /></Field>
            <Field label="Public contact email"><Input testid="input-email-public" value={profile.email_public || ''} onChange={(v) => setProfile({ ...profile, email_public: v })} /></Field>
          </Row>
          <Row>
            <Field label="Verification doc URL (private)"><Input testid="input-docs" placeholder="Upload to drive/dropbox & paste link" value={profile.docs_url || ''} onChange={(v) => setProfile({ ...profile, docs_url: v })} /></Field>
            <Field label="Phone (private)"><Input testid="input-phone" value={profile.phone || ''} onChange={(v) => setProfile({ ...profile, phone: v })} /></Field>
          </Row>
        </Section>

        {/* Project lists */}
        {[
          { key: 'current_projects', status: 'current', title: 'Current projects' },
          { key: 'upcoming_projects', status: 'upcoming', title: 'Upcoming projects' },
          { key: 'past_projects', status: 'past', title: 'Past projects' },
        ].map(({ key, status, title }) => (
          <Section key={key} title={title} action={
            <button data-testid={`add-${status}-btn`} onClick={() => addProject(key, status)} className="text-xs bg-white/10 border border-white/15 rounded-full px-3 py-1.5 flex items-center gap-1 hover:bg-white/15">
              <Plus size={12} weight="bold" /> Add
            </button>
          }>
            {(profile[key] || []).length === 0 ? (
              <p className="text-xs text-white/70 italic">No {title.toLowerCase()} yet.</p>
            ) : (
              <div className="space-y-2" data-testid={`${status}-list`}>
                {(profile[key] || []).map((p, idx) => (
                  <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center bg-black/30 border border-white/8 rounded-lg p-3">
                    <input data-testid={`${status}-title-${idx}`} placeholder="Title" value={p.title || ''} onChange={(e) => updateProject(key, idx, 'title', e.target.value)} className={`${inputCls} sm:col-span-4`} />
                    <input data-testid={`${status}-role-${idx}`} placeholder="Role" value={p.role || ''} onChange={(e) => updateProject(key, idx, 'role', e.target.value)} className={`${inputCls} sm:col-span-3`} />
                    <select data-testid={`${status}-industry-${idx}`} value={p.industry || ''} onChange={(e) => updateProject(key, idx, 'industry', e.target.value)} className={`${inputCls} sm:col-span-2`}>
                      <option value="">Industry</option>
                      {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                    <input data-testid={`${status}-year-${idx}`} type="number" placeholder="Year" value={p.year || ''} onChange={(e) => updateProject(key, idx, 'year', e.target.value)} className={`${inputCls} sm:col-span-2`} />
                    <button aria-label="Remove credit" data-testid={`${status}-remove-${idx}`} onClick={() => removeProject(key, idx)} className="sm:col-span-1 text-rose-400 hover:text-rose-300 flex items-center justify-center"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        ))}
      </div>
    </div>
  );
};

const inputCls = 'bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm placeholder:text-white/50 focus:border-[#c084fc] outline-none w-full';
const Input = ({ testid, value, onChange, type = 'text', placeholder, min, id }) => (
  <input id={id} data-testid={testid} type={type} min={min} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} />
);
// A single input/select/textarea child is tied to the label via id; anything
// else (chip grids, multi-control rows) becomes a group named by the label.
const Field = ({ label, children }) => {
  const id = useId();
  const single = Children.count(children) === 1 && isValidElement(children)
    && (children.type === Input || ['input', 'select', 'textarea'].includes(children.type));
  const labelCls = 'text-[10px] uppercase tracking-wider text-white/70 font-bold';
  if (single) {
    return (
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        <label htmlFor={id} className={labelCls}>{label}</label>
        {cloneElement(children, { id })}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5 flex-1 min-w-0" role="group" aria-labelledby={id}>
      <span id={id} className={labelCls}>{label}</span>
      {children}
    </div>
  );
};
const Row = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
const Section = ({ title, action, children }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-3">
    <div className="flex items-center justify-between">
      <h2 className="text-xs uppercase tracking-wider text-white/70 font-bold">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

export default FraternityProfileEdit;
